import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, Users, ArrowRight, Building2, CheckCircle2 } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import Seo from "../lib/Seo";
import CenterCard from "../components/CenterCard";
import Breadcrumbs from "../components/Breadcrumbs";
import { fetchCenters } from "../lib/api";
import { getRegionBySlug, REGIONS } from "../lib/regions";
import { SEO_CITIES } from "../lib/seoPages";

export default function RegionDetail() {
    const { slug } = useParams();
    const region = getRegionBySlug(slug);

    const [centers, setCenters] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!region) return;
        const controller = new AbortController();
        let cancelled = false;
        setLoading(true);
        fetchCenters(
            { region: region.name, page: 1, limit: 12 },
            { signal: controller.signal },
        )
            .then((data) => {
                if (cancelled) return;
                setCenters(data.centers || []);
                setTotal(data.total || 0);
            })
            .catch(() => {
                if (cancelled) return;
                setCenters([]);
                setTotal(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [region]);

    if (!region) {
        return <Navigate to="/regiones" replace />;
    }

    const h1 = `Centros de rehabilitación de drogas en ${region.shortName}`;
    const description = `Directorio de centros de rehabilitación de drogas en ${region.shortName}, Chile. Compara modalidades (residencial, ambulatorio, online), precios y servicios. Información clínica verificada.`;

    const faqs = [
        {
            q: `¿Cuántos centros de rehabilitación de drogas hay en ${region.shortName}?`,
            a: `Actualmente contamos con ${total || "varios"} centros registrados en ${region.shortName}, distribuidos principalmente en ${region.mainCities.slice(0, 3).join(", ")}. La oferta incluye modalidades residencial, ambulatoria y online.`,
        },
        {
            q: `¿Cuánto cuesta un tratamiento de rehabilitación en ${region.shortName}?`,
            a: `Los precios varían según el centro y la modalidad. En residencial suelen ir entre $1.500.000 y $4.500.000 mensuales. El ambulatorio intensivo oscila entre $250.000 y $900.000 mensuales, y los programas online desde $150.000 mensuales. Siempre pide presupuesto escrito detallado.`,
        },
        {
            q: `¿Los centros en ${region.shortName} están acreditados?`,
            a: `La mayoría de los centros listados cuentan con acreditación de la SEREMI de Salud correspondiente y varios tienen convenios con SENDA. Busca el badge "Verificado" en cada ficha antes de tomar una decisión.`,
        },
        {
            q: "¿Puedo iniciar un tratamiento sin ser de la región?",
            a: `Sí. Muchos centros de ${region.shortName} reciben pacientes de otras regiones del país, especialmente en modalidad residencial. Algunos incluso ofrecen acompañamiento con traslado desde el aeropuerto más cercano.`,
        },
    ];

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    // Related regions (up to 3) for internal linking
    const related = REGIONS.filter((r) => r.slug !== region.slug).slice(0, 3);

    // Cities in this region (from our generated SEO pages)
    const citiesInRegion = Object.values(SEO_CITIES).filter(
        (c) => c.regionSlug === region.slug,
    );

    return (
        <>
            <Seo
                title={h1}
                description={description}
                path={`/regiones/${region.slug}`}
                keywords={`centros de rehabilitación de drogas ${region.shortName}, rehabilitación ${region.capital}, tratamiento drogas ${region.shortName}, clínica de desintoxicación ${region.capital}`}
                jsonLd={faqJsonLd}
            />

            <section className="border-b border-slate-800 bg-slate-950">
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                    <Breadcrumbs
                        items={[
                            { name: "Inicio", path: "/" },
                            { name: "Regiones", path: "/regiones" },
                            { name: region.shortName },
                        ]}
                    />
                    <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-medium text-cyan-300">
                        <MapPin className="h-3.5 w-3.5" />
                        Región de Chile · Capital: {region.capital}
                    </div>
                    <h1
                        data-testid="region-h1"
                        className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl"
                    >
                        {h1}
                    </h1>
                    <p className="mt-4 max-w-3xl text-base text-slate-400 sm:text-lg">
                        {region.intro}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
                        <div className="inline-flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900 px-3 py-2">
                            <Building2 className="h-4 w-4 text-cyan-400" />
                            <span className="text-slate-200">
                                <strong data-testid="region-center-count">
                                    {loading ? "—" : total}
                                </strong>{" "}
                                centros registrados
                            </span>
                        </div>
                        <div className="inline-flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900 px-3 py-2">
                            <Users className="h-4 w-4 text-cyan-400" />
                            <span className="text-slate-200">{region.population}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contexto editorial */}
            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Contexto del tratamiento en {region.shortName}
                    </h2>
                    <p className="mt-4 leading-relaxed text-slate-300">
                        {region.context}
                    </p>
                    {region.mainCities.length > 0 ? (
                        <div className="mt-6">
                            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
                                Principales ciudades cubiertas
                            </h3>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {region.mainCities.map((c) => {
                                    // Prefer linking to dedicated city page (indexable)
                                    // and only fall back to filtered /centros with nofollow
                                    // when no dedicated page exists.
                                    const dedicated = citiesInRegion.find(
                                        (city) => city.name === c,
                                    );
                                    if (dedicated) {
                                        return (
                                            <Link
                                                key={c}
                                                to={`/ciudades/${dedicated.slug}`}
                                                data-testid={`region-city-${c}`}
                                                className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-200 hover:border-cyan-500/50 hover:text-cyan-400"
                                            >
                                                <MapPin className="h-3 w-3" /> {c}
                                            </Link>
                                        );
                                    }
                                    return (
                                        <Link
                                            key={c}
                                            to={`/centros?region=${encodeURIComponent(region.name)}&city=${encodeURIComponent(c)}`}
                                            rel="nofollow"
                                            data-testid={`region-city-${c}`}
                                            className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-200 hover:border-cyan-500/50 hover:text-cyan-400"
                                        >
                                            <MapPin className="h-3 w-3" /> {c}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {citiesInRegion.length > 0 ? (
                        <div className="mt-8 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-5">
                            <h3 className="text-sm font-semibold uppercase tracking-wider text-cyan-300">
                                Páginas dedicadas por ciudad en esta región
                            </h3>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                                {citiesInRegion.map((c) => (
                                    <Link
                                        key={c.slug}
                                        to={`/ciudades/${c.slug}`}
                                        data-testid={`region-city-page-${c.slug}`}
                                        className="group flex items-center justify-between rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 hover:border-cyan-500/40 hover:text-cyan-400"
                                    >
                                        <span className="font-medium">{c.name}</span>
                                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>

            {/* Centros en la región */}
            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                                Centros disponibles en {region.shortName}
                            </h2>
                            <p className="mt-2 text-slate-400">
                                {loading
                                    ? "Cargando centros..."
                                    : total > 0
                                      ? `${total} centro${total === 1 ? "" : "s"} verificados, con distintos enfoques clínicos y modalidades.`
                                      : "Aún no tenemos centros registrados directamente en esta región. Consulta el directorio general o contáctanos."}
                            </p>
                        </div>
                        <Link
                            to={`/centros?region=${encodeURIComponent(region.name)}`}
                            rel="nofollow"
                            data-testid="region-see-all"
                            className="inline-flex items-center gap-1 text-sm font-medium text-cyan-400 hover:text-cyan-300"
                        >
                            Ver todos con filtros <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div
                        className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
                        data-testid="region-centers-grid"
                    >
                        {loading ? (
                            Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="h-80 animate-pulse rounded-xl border border-slate-800 bg-slate-900"
                                />
                            ))
                        ) : centers.length > 0 ? (
                            centers.map((c) => <CenterCard key={c.center_id} center={c} />)
                        ) : (
                            <div className="col-span-full rounded-xl border border-dashed border-slate-800 bg-slate-900 p-10 text-center">
                                <p className="text-slate-300">
                                    No hay centros registrados en {region.shortName} en este momento.
                                </p>
                                <Link to="/centros" className="mt-4 inline-block">
                                    <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-400">
                                        Ver directorio completo
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Por qué elegir */}
            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Qué revisar al elegir un centro en {region.shortName}
                    </h2>
                    <ul className="mt-6 space-y-3">
                        {[
                            `Acreditación de la SEREMI de Salud de ${region.shortName} y registro SENDA cuando corresponda.`,
                            "Equipo multidisciplinario con al menos médico/psiquiatra, psicólogo clínico y terapeuta en adicciones.",
                            "Programa con evaluación inicial, tratamiento estructurado y seguimiento post-alta por mínimo 6 meses.",
                            "Presupuesto escrito transparente, especificando qué incluye y qué queda fuera.",
                            "Protocolos explícitos contra tratos degradantes y política de visitas familiares.",
                        ].map((txt, i) => (
                            <li
                                key={i}
                                className="flex gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4"
                            >
                                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-400" />
                                <span className="text-slate-300">{txt}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* FAQ */}
            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Preguntas frecuentes sobre centros en {region.shortName}
                    </h2>
                    <Accordion
                        type="single"
                        collapsible
                        className="mt-6 rounded-xl border border-slate-800 bg-slate-900"
                        data-testid="region-faq"
                    >
                        {faqs.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`r-faq-${i}`}
                                className="border-slate-800 px-5"
                            >
                                <AccordionTrigger className="text-left text-slate-100 hover:text-cyan-400">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-400">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Otras regiones */}
            <section className="py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Explora otras regiones
                    </h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={`/regiones/${r.slug}`}
                                data-testid={`related-region-${r.slug}`}
                                className="card-hover rounded-xl border border-slate-800 bg-slate-900 p-5"
                            >
                                <div className="flex items-center gap-2 text-cyan-400">
                                    <MapPin className="h-4 w-4" />
                                    <span className="text-xs font-medium uppercase tracking-wider">
                                        Región
                                    </span>
                                </div>
                                <h3 className="mt-3 text-lg font-semibold text-slate-50">
                                    {r.shortName}
                                </h3>
                                <p className="mt-2 text-xs text-slate-400">
                                    Capital: {r.capital}
                                </p>
                            </Link>
                        ))}
                        <Link
                            to="/regiones"
                            className="card-hover flex items-center justify-center rounded-xl border border-dashed border-slate-700 bg-slate-900 p-5 text-sm text-cyan-400 hover:text-cyan-300"
                        >
                            Ver las 16 regiones <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
